import { inject, injectable } from 'inversify'
import { comparer, reaction } from 'mobx'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import { RouterRepository } from '@core/routing/router.repository'
import { FiltersConfig, type FilterScope, type ScopeDefinition } from './filters.config'
import { FiltersRepository } from './filters.repository'
import { filtersToQuery, queryToFilters } from './filters.helpers'

/**
 * Keeps the address bar and the narrowing of a list agreeing with each other.
 *
 * One service for every list rather than a step in seven Presenters: a page that
 * forgot to save itself would be indistinguishable from a page with nothing to
 * save, and that is precisely the kind of gap nobody notices until someone
 * refreshes. Which list a route shows is declared once, in the route table.
 *
 * What this buys, none of which the in-memory Repository could: a refresh keeps
 * the view, Back works, and a narrowed list can be sent to someone else. It is
 * also the shape server-side filtering needs, since a query string is already
 * what would be sent.
 */
@injectable()
export class FilterUrlService implements AppLifecycle {
  @inject(RouterRepository)
  private routerRepository!: RouterRepository

  @inject(FiltersRepository)
  private filtersRepository!: FiltersRepository

  @inject(FiltersConfig)
  private filtersConfig!: FiltersConfig

  private started = false
  /** Set while restoring, so the URL we just read is not immediately rewritten. */
  private restoring = false

  initialize(): void {
    if (this.started) return
    this.started = true

    reaction(
      () => this.routerRepository.currentRoute,
      () => this.onRouteCommitted(),
      { fireImmediately: true },
    )

    reaction(
      () => {
        const scope = this.currentScope()
        return scope === null ? null : ([scope, this.filtersRepository.activeFor(scope)] as const)
      },
      (current) => {
        if (current === null || this.restoring) return
        const [scope, active] = current
        const definition = this.filtersConfig.definitionFor(scope)
        this.routerRepository.replaceQuery({
          ...this.unownedQuery(definition),
          ...filtersToQuery(active, definition),
        })
      },
      // Every other scope shares one observable.ref map (FiltersRepository),
      // so any scope's write produces a brand-new tuple reference here even
      // when the current route's own scope is untouched — structural
      // equality is what keeps a filter change on scope A from rewriting the
      // URL for a route whose scope is B (or, for a route with no list at
      // all, its own filterScope shouldn't be reacting to A in the first
      // place, but the same reference churn would still fire it otherwise).
      { equals: comparer.structural },
    )
  }

  /**
   * `filterScope` may be a plain scope or, for a route whose list itself
   * depends on the URL (Teams' mine/admin segment), a function of the
   * current query — resolved with what is committed *right now*, since a
   * scope that could see its own not-yet-written output would be circular.
   */
  private currentScope(): FilterScope | null {
    const routeId = this.routerRepository.currentRoute.routeId
    if (routeId === null) return null
    const filterScope = this.routerRepository.findRoute(routeId)?.filterScope
    if (filterScope === undefined) return null
    return typeof filterScope === 'function' ? filterScope(this.routerRepository.currentRoute.query) : filterScope
  }

  /**
   * `replaceQuery` replaces the whole query string — so a key this scope's
   * own definition does not own (Teams' `?segment=`, say) would otherwise be
   * silently dropped the moment a filter changes. Everything currently in
   * the URL that `filtersToQuery` would not itself produce survives into the
   * rewrite; `filtersToQuery`'s own keys are spread on top of it, so they
   * still win if a stale value of the same name is sitting in the URL.
   */
  private unownedQuery(definition: ScopeDefinition): Record<string, string> {
    const owned = new Set([...definition.filters.map((filter) => filter.id), 'page', 'sort', 'dir'])
    return Object.fromEntries(
      Object.entries(this.routerRepository.currentRoute.query).filter(([key]) => !owned.has(key)),
    )
  }

  /**
   * A URL carrying filters wins; an empty one does not.
   *
   * The asymmetry is deliberate. Arriving with `?text=drill` means *this* view,
   * so it is restored. Arriving with a bare path — clicking the sidebar link —
   * must not silently clear the narrowing the user left on the page, which the
   * Repository has deliberately kept since before URLs were involved. In that
   * case the URL is the one that is out of date, so it is brought up to match.
   */
  private onRouteCommitted(): void {
    const scope = this.currentScope()
    if (scope === null) return

    const definition = this.filtersConfig.definitionFor(scope)
    const query = this.routerRepository.currentRoute.query
    const fromUrl = queryToFilters(query, definition)
    const carriesFilters = Object.keys(filtersToQuery(fromUrl, definition)).length > 0

    if (!carriesFilters) {
      this.routerRepository.replaceQuery({
        ...this.unownedQuery(definition),
        ...filtersToQuery(this.filtersRepository.activeFor(scope), definition),
      })
      return
    }

    this.restoring = true
    try {
      this.filtersRepository.restore(scope, fromUrl)
    } finally {
      this.restoring = false
    }
  }
}
